import { useIonActionSheet } from '@ionic/react';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';

import { useAuth } from '@/contexts/AuthenticationContext';
import { useClinic } from '@/contexts/ClinicContext';
import { getClinic } from '@/data/Clinics';
import {
  DentistHour,
  getDentistHoursByDentistId,
  updateDentistHour
} from '@/data/DentistHours';
import { getUnreadNotificationsCount } from '@/data/DentistNotifications';
import {
  Dentist,
  getDentistsByClinicId,
  getDentistsByUserId,
  updateDentist,
  updateDentistShowCalendarTips,
  updateDentistShowProfileTips
} from '@/data/Dentists';
import { useToast } from '@/hooks';
import { formatDentistName } from '@/utils/helpers/formatDentistName';
import { formatName } from '@/utils/helpers/formatName';
import {
  UseMutationResult,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';

interface DentistContextType {
  dentists: Dentist[];
  setDentists: React.Dispatch<React.SetStateAction<Dentist[]>>;
  selectedDentist: Dentist | null;
  setSelectedDentist: (dentist: Dentist | null) => void;
  dentistHours: DentistHour[];
  selectDentist: (dentist: Dentist | null) => Promise<void>;
  updateHours: (hours: DentistHour[]) => Promise<void>;
  fetchDentists: () => void;
  loading: boolean;
  reloading: boolean;
  loaded: boolean;
  onSwitch: () => void;
  updateDentistMutation: UseMutationResult<
    Dentist,
    Error,
    { dentistId: string; dentist: Dentist }
  >;
  updateProfileTipsMutation: UseMutationResult<Dentist, Error, string>;
  updateCalendarTipsMutation: UseMutationResult<Dentist, Error, string>;
}

const DentistContext = createContext<DentistContextType | undefined>(undefined);

interface DentistProviderProps {
  children: React.ReactNode;
}

export const DentistProvider: React.FC<DentistProviderProps> = ({
  children
}) => {
  const { authenticated, user } = useAuth();
  const { selectedClinic: clinic, selectClinic, isClinic } = useClinic();
  const displayToast = useToast();
  const queryClient = useQueryClient();

  const [selectedDentist, setSelectedDentist] = useState<Dentist | null>(null);
  const [present] = useIonActionSheet();

  const updateDentistMutation = useMutation({
    mutationFn: async ({
      dentistId,
      dentist
    }: {
      dentistId: string;
      dentist: Dentist;
    }) => {
      const result = await updateDentist({ dentistId, dentist });
      if (!result) {
        throw new Error('Failed to update dentist');
      }
      return result;
    },
    onSuccess: (updatedDentist) => {
      queryClient.setQueryData(
        ['dentists', clinic?.id, user?.id, isClinic],
        (oldDentists: Dentist[] | undefined) => {
          if (!oldDentists) return [updatedDentist];
          return oldDentists.map((dentist) =>
            dentist.id === updatedDentist.id ? updatedDentist : dentist
          );
        }
      );

      if (selectedDentist?.id === updatedDentist.id) {
        setSelectedDentist(updatedDentist);
      }
    },
    onError: () => {
      displayToast({
        message:
          'Failed to update dentist. Please check your internet connection and try again.',
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
    }
  });

  const updateProfileTipsMutation = useMutation({
    mutationFn: async (dentistId: string) => {
      const result = await updateDentistShowProfileTips(dentistId, false);
      if (!result) {
        throw new Error('Failed to update profile tips visibility');
      }
      return result;
    },
    onSuccess: (updatedDentist) => {
      queryClient.setQueryData(
        ['dentists', clinic?.id, user?.id, isClinic],
        (oldDentists: Dentist[] | undefined) => {
          if (!oldDentists) return [updatedDentist];
          return oldDentists.map((dentist) =>
            dentist.id === updatedDentist.id ? updatedDentist : dentist
          );
        }
      );

      if (selectedDentist?.id === updatedDentist.id) {
        setSelectedDentist(updatedDentist);
      }
    },
    onError: () => {
      displayToast({
        message: 'Failed to update tips visibility. Please try again.',
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
    }
  });

  const updateCalendarTipsMutation = useMutation({
    mutationFn: async (dentistId: string) => {
      const result = await updateDentistShowCalendarTips(dentistId, false);
      if (!result) {
        throw new Error('Failed to update calendar tips visibility');
      }
      return result;
    },
    onSuccess: (updatedDentist) => {
      queryClient.setQueryData(
        ['dentists', clinic?.id, user?.id, isClinic],
        (oldDentists: Dentist[] | undefined) => {
          if (!oldDentists) return [updatedDentist];
          return oldDentists.map((dentist) =>
            dentist.id === updatedDentist.id ? updatedDentist : dentist
          );
        }
      );

      if (selectedDentist?.id === updatedDentist.id) {
        setSelectedDentist(updatedDentist);
      }
    },
    onError: () => {
      displayToast({
        message: 'Failed to update tips visibility. Please try again.',
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
    }
  });

  const fetchDentistBadge = useCallback(
    async ({ dentistId }: { dentistId: string | undefined }) => {
      if (!dentistId) return;

      try {
        return await queryClient.fetchQuery({
          queryKey: ['unreadNotificationsCount', dentistId],
          queryFn: () => getUnreadNotificationsCount({ dentistId }),
          staleTime: 0
        });
      } catch {
        displayToast({
          message: `Please check your internet connection.`,
          duration: 3000,
          position: 'bottom'
        });
        return 0;
      }
    },
    []
  );

  const formatDentistsData = useCallback(
    async (dentists: Dentist[]) => {
      return await Promise.all(
        dentists
          .sort((a, b) => {
            const nameComparison = a.first_name.localeCompare(b.first_name);
            if (nameComparison !== 0) return nameComparison;
            return a.clinic?.name?.localeCompare(b.clinic?.name || '') || 0;
          })
          .map(async (dentist) => ({
            ...dentist,
            badge: await fetchDentistBadge({ dentistId: dentist.id })
          }))
      );
    },
    [fetchDentistBadge]
  );

  const {
    data: dentists = [],
    isLoading: loading,
    isFetched: loaded,
    refetch: fetchDentists,
    isRefetching: reloading
  }: UseQueryResult<Dentist[], Error> = useQuery({
    queryKey: ['dentists', clinic?.id, user?.id, isClinic],
    queryFn: async () => {
      if (isClinic === undefined) return [];

      try {
        if (clinic?.id && isClinic) {
          const data = await getDentistsByClinicId(clinic.id!);
          return data.length > 0 ? await formatDentistsData(data) : [];
        } else if (user?.id) {
          const data = await getDentistsByUserId(user.id);
          return data.length > 0 ? await formatDentistsData(data) : [];
        }
        return [];
      } catch (error) {
        displayToast({
          message: `Please check your internet connection.`,
          duration: 3000,
          position: 'bottom'
        });
        throw error;
      }
    },
    enabled: authenticated && isClinic !== undefined && (isClinic ? !!clinic?.id : !!user?.id)
  });

  const setDentists: React.Dispatch<React.SetStateAction<Dentist[]>> =
    useCallback(
      (dentists) => {
        queryClient.setQueryData(
          ['dentists', clinic?.id, user?.id, isClinic],
          dentists
        );
      },
      [queryClient, dentists, clinic?.id, user?.id, isClinic]
    );

  const { data: dentistHours = [] }: UseQueryResult<DentistHour[], Error> =
    useQuery({
      queryKey: ['dentistHours', selectedDentist?.id],
      queryFn: async () => {
        if (!selectedDentist?.id) return [];

        try {
          return await getDentistHoursByDentistId(selectedDentist.id!);
        } catch (error) {
          displayToast({
            message: `Please check your internet connection.`,
            duration: 3000,
            position: 'bottom',
            positionAnchor: 'tab-bar'
          });
          throw error;
        }
      },
      enabled: !!selectedDentist?.id
    });

  const selectDentist = useCallback(
    async (dentist: Dentist | null) => {
      if (dentist === null) {
        setSelectedDentist(null);
        // Dentist-only accounts drop the clinic when clearing a selection.
        // Clinic accounts (and first login, while identity is still unknown)
        // must keep the clinic so the picker can load dentists.
        if (isClinic === false) {
          selectClinic(null);
        }
        return;
      }

      if (isClinic === undefined) return;

      if (isClinic) {
        setSelectedDentist(dentist);
        return;
      }

      const clinicData = await queryClient.fetchQuery({
        queryKey: ['clinic', dentist?.clinic_id],
        queryFn: () => getClinic(dentist?.clinic_id!),
        staleTime: 0
      });
      if (!clinicData) {
        displayToast({
          message: 'Failed to load clinic. Please try again.',
          duration: 3000,
          position: 'bottom'
        });
        return;
      }
      selectClinic(clinicData);
      setSelectedDentist(dentist);
    },
    [isClinic, selectClinic, queryClient, displayToast]
  );

  const updateHours = useCallback(
    async (hours: DentistHour[]) => {
      if (selectedDentist) {
        try {
          const existingHours = await queryClient.fetchQuery({
            queryKey: ['dentistHoursByDentistId', selectedDentist.id],
            queryFn: () => getDentistHoursByDentistId(selectedDentist.id!)
          });
          await Promise.all(
            hours.map(async (hour) => {
              const existingHour = existingHours.find(
                (existingHour) => existingHour.day === hour.day
              );
              return existingHour
                ? updateDentistHour({
                    id: existingHour.id!,
                    dentistHour: hour
                  })
                : null;
            })
          );

          queryClient.invalidateQueries({
            queryKey: ['dentistHours', selectedDentist.id]
          });

          displayToast({
            message: 'Working hours updated successfully.',
            duration: 5000,
            position: 'bottom',
            positionAnchor: 'tab-bar'
          });
        } catch {
          displayToast({
            message:
              'Failed to update working hours. Please check your internet connection and try again.',
            duration: 5000,
            position: 'bottom',
            positionAnchor: 'tab-bar'
          });
        }
      }
    },
    [selectedDentist, queryClient]
  );

  const onSwitch = useCallback(() => {
    if (isClinic === undefined) return;

    const options = isClinic
      ? dentists.map((dentist) => {
          const formattedDentistName = formatDentistName({
            dentist,
            returnDentistFullName: true
          });

          return {
            text: formattedDentistName,
            handler: () => {
              selectDentist(dentist);
              displayToast({
                message: `Switched to ${formattedDentistName}`,
                duration: 2400,
                position: 'bottom',
                positionAnchor: 'tab-bar'
              });
            }
          };
        })
      : dentists.map((dentist) => ({
          text: dentist?.clinic?.name ? formatName(dentist.clinic.name) : '',
          handler: () => {
            selectDentist(dentist);
            displayToast({
              message: `Switched to ${dentist?.clinic?.name ? formatName(dentist.clinic.name) : ''}`,
              duration: 2400,
              position: 'bottom',
              positionAnchor: 'tab-bar'
            });
          }
        }));

    present({
      header: `Switch ${isClinic ? 'Dentist' : 'Clinic'}`,
      subHeader: `Choose a ${isClinic ? 'dentist' : 'clinic'} below`,
      buttons: [
        ...options,
        {
          text: 'Cancel',
          role: 'cancel'
        }
      ]
    });
  }, [isClinic, dentists, selectDentist, present]);

  useEffect(() => {
    if (!user?.id) selectDentist(null);
  }, [user?.id, selectDentist]);

  return (
    <DentistContext.Provider
      value={{
        dentists,
        setDentists,
        selectedDentist,
        setSelectedDentist,
        dentistHours,
        selectDentist,
        updateHours,
        fetchDentists,
        loading: loading || reloading,
        reloading,
        loaded,
        onSwitch,
        updateDentistMutation,
        updateProfileTipsMutation,
        updateCalendarTipsMutation
      }}
    >
      {children}
    </DentistContext.Provider>
  );
};

export const useDentist = () => {
  const context = useContext(DentistContext);
  if (context === undefined) {
    throw new Error('useDentist must be used within a DentistProvider');
  }
  return context;
};
